import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiTrash2, FiPlus, FiCalendar, FiFilter, FiPackage, FiDroplet,
  FiX, FiAlertTriangle, FiDollarSign
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Format quantity to avoid floating point display issues
const formatQuantity = (qty) => {
  if (qty === null || qty === undefined) return '0';
  // Round to 3 decimal places, remove trailing zeros
  const rounded = Math.round(qty * 1000) / 1000;
  return rounded.toString().replace(/\.?0+$/, '');
};

const WastedItemsManagement = () => {
  const [wastedItems, setWastedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalLoss, setTotalLoss] = useState(0);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterReason, setFilterReason] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [addForm, setAddForm] = useState({
    itemType: 'ingredient',
    itemId: '',
    quantity: 0,
    reason: 'damaged',
    note: ''
  });
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const reasonLabels = {
    expired: 'Hết hạn',
    damaged: 'Hư hỏng',
    end_of_day: 'Hủy cuối ngày',
    quality: 'Không đạt chất lượng',
    other: 'Khác'
  };

  const reasonColors = {
    expired: 'bg-red-100 text-red-700',
    damaged: 'bg-orange-100 text-orange-700',
    end_of_day: 'bg-purple-100 text-purple-700',
    quality: 'bg-yellow-100 text-yellow-700',
    other: 'bg-gray-100 text-gray-700'
  };

  // Vietnamese unit labels
  const packagingLabels = { box: 'Hộp', can: 'Lon', bottle: 'Chai', bag: 'Túi', piece: 'Quả', item: 'Cái', kg: 'Kg' };
  const supplyUnitLabels = { piece: 'Cái', box: 'Hộp', pack: 'Gói', roll: 'Cuộn', bag: 'Túi', bottle: 'Chai', can: 'Lon', set: 'Bộ' };

  const getUnitLabel = (unit, isSupply = false) => {
    if (isSupply) {
      return supplyUnitLabels[unit] || unit;
    }
    return packagingLabels[unit] || unit;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadWastedItems();
  }, [startDate, endDate, filterReason, filterType]);

  const loadWastedItems = async () => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (filterReason !== 'all') params.reason = filterReason;
      if (filterType !== 'all') params.itemType = filterType;

      const response = await axios.get(`${API_URL}/wasted-items`, { params });
      setWastedItems(response.data.data || []);
      setTotalLoss(response.data.summary?.totalLoss || 0);
    } catch (error) {
      toast.error('Không thể tải danh sách hàng hủy');
    } finally {
      setLoading(false);
    }
  };

  const loadItemsForAdd = async () => {
    try {
      const [ingRes, supRes] = await Promise.all([
        axios.get(`${API_URL}/ingredients`, { params: { limit: 0 } }),
        axios.get(`${API_URL}/supplies`, { params: { limit: 0 } })
      ]);
      setIngredients(ingRes.data.data || []);
      setSupplies(supRes.data.data || []);
    } catch (error) {
      toast.error('Không thể tải danh sách mặt hàng');
    }
  };

  const openAddModal = () => {
    loadItemsForAdd();
    setAddForm({
      itemType: 'ingredient',
      itemId: '',
      quantity: 0,
      reason: 'damaged',
      note: ''
    });
    setSelectedItem(null);
    setShowAddModal(true);
  };

  const handleItemSelect = (e) => {
    const itemId = e.target.value;
    setAddForm(prev => ({ ...prev, itemId }));
    
    if (addForm.itemType === 'ingredient') {
      const item = ingredients.find(i => i._id === itemId);
      setSelectedItem(item);
    } else {
      const item = supplies.find(s => s._id === itemId);
      setSelectedItem(item);
    }
  };

  const handleAddWasted = async () => {
    if (!addForm.itemId || addForm.quantity <= 0) {
      toast.error('Vui lòng chọn mặt hàng và nhập số lượng');
      return;
    }

    try {
      const payload = {
        itemType: addForm.itemType,
        quantity: addForm.quantity,
        reason: addForm.reason,
        note: addForm.note
      };

      if (addForm.itemType === 'ingredient') {
        payload.ingredientId = addForm.itemId;
      } else {
        payload.supplyId = addForm.itemId;
      }

      await axios.post(`${API_URL}/wasted-items`, payload);
      toast.success('Đã ghi nhận hàng hủy');
      setShowAddModal(false);
      loadWastedItems();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi ghi nhận hàng hủy');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc muốn xóa bản ghi này? Tồn kho sẽ được khôi phục.')) return;

    try {
      await axios.delete(`${API_URL}/wasted-items/${id}`);
      toast.success('Đã xóa và khôi phục tồn kho');
      loadWastedItems();
    } catch (error) {
      toast.error('Không thể xóa');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(amount || 0));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : ""}>
      <div className={isMobile ? "px-4 py-4 space-y-4 pb-20" : "space-y-6"}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 md:gap-3">
        <div>
          <h1 className="text-lg md:text-2xl font-bold flex items-center gap-2">
            <FiTrash2 className="text-red-500" />
            Quản lý hàng hủy
          </h1>
          <p className="text-gray-500 text-xs md:text-sm mt-0.5 md:mt-1">Theo dõi và ghi nhận hàng hóa bị hủy</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center justify-center gap-2 px-3 md:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
        >
          <FiPlus size={16} /> Ghi nhận hàng hủy
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <div className="bg-white p-3 md:p-4 rounded-xl shadow-sm border">
          <p className="text-xs md:text-sm text-gray-500">Tổng bản ghi</p>
          <p className="text-xl md:text-2xl font-bold">{wastedItems.length}</p>
        </div>
        <div className="bg-red-50 p-3 md:p-4 rounded-xl shadow-sm border border-red-100">
          <p className="text-xs md:text-sm text-red-600">Tổng thiệt hại</p>
          <p className="text-lg md:text-2xl font-bold text-red-700">{formatCurrency(totalLoss)}đ</p>
        </div>
        <div className="bg-orange-50 p-3 md:p-4 rounded-xl shadow-sm border border-orange-100">
          <p className="text-xs md:text-sm text-orange-600">Hư hỏng</p>
          <p className="text-xl md:text-2xl font-bold text-orange-700">
            {wastedItems.filter(i => i.reason === 'damaged').length}
          </p>
        </div>
        <div className="bg-purple-50 p-3 md:p-4 rounded-xl shadow-sm border border-purple-100">
          <p className="text-xs md:text-sm text-purple-600">Hủy cuối ngày</p>
          <p className="text-xl md:text-2xl font-bold text-purple-700">
            {wastedItems.filter(i => i.reason === 'end_of_day').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-3 md:p-4 rounded-xl shadow-sm border">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <FiCalendar className="text-gray-500 hidden md:block" />
          
          {/* Start Date - sr-only pattern */}
          <div 
            className="relative cursor-pointer"
            onClick={() => {
              const dateInput = document.getElementById('wasted-start-date');
              if (dateInput) dateInput.showPicker();
            }}
          >
            <input
              type="text"
              readOnly
              value={formatDisplayDate(startDate)}
              placeholder="Từ ngày"
              className="w-[100px] md:w-[120px] px-2 md:px-3 py-1.5 md:py-2 border rounded-lg text-xs md:text-sm cursor-pointer placeholder:text-gray-400"
            />
            <input
              id="wasted-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="sr-only"
            />
          </div>
          
          <span className="text-gray-400">→</span>
          
          {/* End Date - sr-only pattern */}
          <div 
            className="relative cursor-pointer"
            onClick={() => {
              const dateInput = document.getElementById('wasted-end-date');
              if (dateInput) dateInput.showPicker();
            }}
          >
            <input
              type="text"
              readOnly
              value={formatDisplayDate(endDate)}
              placeholder="Đến ngày"
              className="w-[100px] md:w-[120px] px-2 md:px-3 py-1.5 md:py-2 border rounded-lg text-xs md:text-sm cursor-pointer placeholder:text-gray-400"
            />
            <input
              id="wasted-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="sr-only"
            />
          </div>
          
          <select
            value={filterReason}
            onChange={(e) => setFilterReason(e.target.value)}
            className="border rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm"
          >
            <option value="all">Tất cả lý do</option>
            {Object.entries(reasonLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm"
          >
            <option value="all">Tất cả loại</option>
            <option value="ingredient">Nguyên liệu</option>
            <option value="supply">Vật dụng</option>
          </select>

          {(startDate || endDate || filterReason !== 'all' || filterType !== 'all') && (
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setFilterReason('all');
                setFilterType('all');
              }}
              className="text-sm text-red-500 hover:text-red-700"
            >
              Xóa bộ lọc
            </button>
          )}
        </div>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
          <p className="text-gray-500 mt-2">Đang tải...</p>
        </div>
      ) : wastedItems.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <FiTrash2 className="mx-auto text-gray-300" size={48} />
          <p className="text-gray-500 mt-4">Chưa có bản ghi hàng hủy nào</p>
        </div>
      ) : isMobile ? (
        // Mobile: Card layout
        <div className="space-y-3">
          {wastedItems.map((item) => (
            <div key={item._id} className="bg-white rounded-xl shadow-sm border p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    item.itemType === 'ingredient' ? 'bg-purple-100' : 'bg-blue-100'
                  }`}>
                    {item.itemType === 'ingredient' ? (
                      <FiDroplet className="text-purple-600" size={16} />
                    ) : (
                      <FiPackage className="text-blue-600" size={16} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(item.wastedAt)}
                      {item.isAutoWasted && (
                        <span className="ml-1 text-purple-600">(Tự động)</span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(item._id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                  title="Xóa và khôi phục tồn kho"
                >
                  <FiX size={16} />
                </button>
              </div>
              
              <div className="mt-2 pt-2 border-t flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${reasonColors[item.reason]}`}>
                    {reasonLabels[item.reason]}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatQuantity(item.quantity)} {getUnitLabel(item.unit, item.itemType === 'supply')}
                  </span>
                </div>
                <span className="font-semibold text-red-600 text-sm">
                  -{formatCurrency(item.lossAmount)}đ
                </span>
              </div>
              
              {item.notes && (
                <p className="mt-2 text-xs text-gray-500 italic">"{item.notes}"</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        // Desktop: Table layout
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Thời gian</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Mặt hàng</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Số lượng</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Thiệt hại</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Lý do</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {wastedItems.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      {formatDate(item.wastedAt)}
                      {item.isAutoWasted && (
                        <span className="ml-2 text-xs text-purple-600">(Tự động)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {item.itemType === 'ingredient' ? (
                          <FiDroplet className="text-purple-500" size={16} />
                        ) : (
                          <FiPackage className="text-blue-500" size={16} />
                        )}
                        <span className="font-medium">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {formatQuantity(item.quantity)} {getUnitLabel(item.unit, item.itemType === 'supply')}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-red-600">
                      {formatCurrency(item.lossAmount)}đ
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${reasonColors[item.reason]}`}>
                        {reasonLabels[item.reason]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title="Xóa và khôi phục tồn kho"
                      >
                        <FiX size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <FiTrash2 className="text-red-500" />
                Ghi nhận hàng hủy
              </h2>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <FiX size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Item Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Loại mặt hàng</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAddForm(prev => ({ ...prev, itemType: 'ingredient', itemId: '' }));
                      setSelectedItem(null);
                    }}
                    className={`flex-1 py-2 rounded-lg border transition-colors ${
                      addForm.itemType === 'ingredient'
                        ? 'bg-purple-50 border-purple-500 text-purple-700'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <FiDroplet className="inline mr-1" /> Nguyên liệu
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAddForm(prev => ({ ...prev, itemType: 'supply', itemId: '' }));
                      setSelectedItem(null);
                    }}
                    className={`flex-1 py-2 rounded-lg border transition-colors ${
                      addForm.itemType === 'supply'
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <FiPackage className="inline mr-1" /> Vật dụng
                  </button>
                </div>
              </div>

              {/* Item Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Chọn mặt hàng *</label>
                <select
                  value={addForm.itemId}
                  onChange={handleItemSelect}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">-- Chọn --</option>
                  {addForm.itemType === 'ingredient'
                    ? ingredients.map(item => (
                        <option key={item._id} value={item._id}>
                          {item.name} (Tồn: {formatQuantity(item.quantity)} {getUnitLabel(item.packagingUnit, false)})
                        </option>
                      ))
                    : supplies.map(item => (
                        <option key={item._id} value={item._id}>
                          {item.name} (Tồn: {formatQuantity(item.stock)} {getUnitLabel(item.unit, true)})
                        </option>
                      ))
                  }
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Số lượng hủy ({selectedItem ? getUnitLabel(selectedItem.packagingUnit || selectedItem.unit, addForm.itemType === 'supply') : 'đơn vị'}) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={addForm.quantity}
                  onChange={(e) => setAddForm(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
                {selectedItem && (
                  <p className="text-sm text-gray-500 mt-1">
                    Thiệt hại ước tính: <span className="font-medium text-red-600">
                      {formatCurrency(addForm.quantity * (selectedItem.defaultPrice || selectedItem.price || 0))}đ
                    </span>
                    <span className="text-xs text-gray-400 block">
                      (Giá thực tế sẽ được tính từ phiếu nhập gần nhất)
                    </span>
                  </p>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium mb-2">Lý do hủy *</label>
                <select
                  value={addForm.reason}
                  onChange={(e) => setAddForm(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  {Object.entries(reasonLabels).filter(([k]) => k !== 'end_of_day').map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium mb-2">Ghi chú</label>
                <textarea
                  value={addForm.note}
                  onChange={(e) => setAddForm(prev => ({ ...prev, note: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Ghi chú thêm (không bắt buộc)"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 border rounded-lg hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleAddWasted}
                  className="flex-1 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Ghi nhận
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default WastedItemsManagement;
